import React, { useEffect, useMemo, useState } from "react";
import {
  Eye,
  LayoutDashboard,
  LogOut,
  Mail,
  Phone,
  Search,
  Users,
  X,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import "./LeadManagement.css";

const API_BASE_URL = (
  process.env.REACT_APP_API_BASE_URL ||
  "https://backendhub.co.in/bcservice"
).replace(/\/+$/, "");

const formatDate = (value) => {
  if (!value) return "—";

  return new Date(value).toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

function LeadManagement() {
  const navigate = useNavigate();

  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [selectedLead, setSelectedLead] = useState(null);

  const session = JSON.parse(
    sessionStorage.getItem("bc_admin_session") || "{}"
  );

  const loadLeads = async () => {
    try {
      setLoading(true);
      setError("");

      const response = await fetch(`${API_BASE_URL}/api/leads/`);
      const json = await response.json();

      if (!response.ok) {
        throw new Error("Unable to load leads.");
      }

      setLeads(json?.items || []);
    } catch (err) {
      console.error("Lead loading failed:", err);
      setError(err.message || "Unable to load leads.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLeads();
  }, []);

  const handleLogout = () => {
    sessionStorage.removeItem("bc_admin_session");
    navigate("/admin/login");
  };

  const normalizedLeads = useMemo(
    () =>
      leads.map((lead) => ({
        ...lead,
        reference:
          lead.reference ||
          `BC-${String(lead.id).padStart(4, "0")}`,
        name:
          lead.fullName ||
          lead.full_name ||
          "Unknown",
        email:
          lead.email ||
          "—",
        phone:
          lead.phone ||
          lead.mobile ||
          "—",
        company:
          lead.companyName ||
          lead.company_name ||
          "—",
        project:
          lead.projectType ||
          lead.project_type ||
          "Not specified",
        status:
          lead.leadStatus ||
          lead.lead_status ||
          "NEW",
        createdAt:
          lead.createdAt ||
          lead.created_at,
        source:
          lead.acquisition?.source === "WEBSITE_BOT"
            ? "Website BOT"
            : "Contact Form",
      })),
    [leads]
  );

  const filteredLeads = useMemo(() => {
    const q = searchText.trim().toLowerCase();

    return normalizedLeads.filter((lead) => {
      const matchesStatus =
        statusFilter === "ALL" ||
        lead.status === statusFilter;

      const matchesSearch =
        !q ||
        [
          lead.reference,
          lead.name,
          lead.email,
          lead.phone,
          lead.company,
          lead.project,
        ]
          .join(" ")
          .toLowerCase()
          .includes(q);

      return matchesStatus && matchesSearch;
    });
  }, [normalizedLeads, searchText, statusFilter]);

  return (
    <div className="bc-lead-shell">
      <aside className="bc-lead-sidebar">
        <div className="bc-lead-brand">
          <div className="bc-lead-logo">BC</div>
          <div>
            <strong>Bairagi</strong>
            <span>Consultants</span>
          </div>
        </div>

        <nav className="bc-lead-nav">
          <button onClick={() => navigate("/admin")}>
            <LayoutDashboard size={17} />
            Dashboard
          </button>

          <button className="active">
            <Users size={17} />
            Lead Management
          </button>
        </nav>

        <div className="bc-lead-profile">
          <div className="bc-lead-avatar">A</div>
          <div>
            <strong>Administrator</strong>
            <span>{session.email || "Admin"}</span>
          </div>
        </div>

        <button className="bc-lead-logout" onClick={handleLogout}>
          <LogOut size={16} />
          Logout
        </button>
      </aside>

      <main className="bc-lead-main">
        <header className="bc-lead-topbar">
          <div>
            <span>LEAD OPERATIONS</span>
            <h1>Lead Management</h1>
            <p>Search, review and act on consultation enquiries.</p>
          </div>

          <div className="bc-lead-count">
            {filteredLeads.length} leads
          </div>
        </header>

        <section className="bc-lead-toolbar">
          <div className="bc-lead-search">
            <Search size={15} />
            <input
              value={searchText}
              onChange={(event) =>
                setSearchText(event.target.value)
              }
              placeholder="Search reference, customer, company, project..."
            />
          </div>

          <select
            value={statusFilter}
            onChange={(event) =>
              setStatusFilter(event.target.value)
            }
          >
            <option value="ALL">All Status</option>
            <option value="NEW">New</option>
            <option value="CONTACTED">Contacted</option>
            <option value="QUALIFIED">Qualified</option>
            <option value="PROPOSAL_SENT">Proposal Sent</option>
            <option value="WON">Won</option>
            <option value="LOST">Lost</option>
          </select>

          <button onClick={loadLeads}>
            Refresh
          </button>
        </section>

        {loading && (
          <div className="bc-lead-message">
            Loading leads...
          </div>
        )}

        {error && (
          <div className="bc-lead-message error">
            {error}
          </div>
        )}

        <section className="bc-lead-grid-card">
          <div className="bc-lead-grid bc-lead-grid-head">
            <span>Reference</span>
            <span>Customer</span>
            <span>Company</span>
            <span>Project</span>
            <span>Source</span>
            <span>Status</span>
            <span>Date</span>
            <span>Actions</span>
          </div>

          {filteredLeads.map((lead) => (
            <div
              className="bc-lead-grid bc-lead-grid-row"
              key={lead.id}
            >
              <strong>{lead.reference}</strong>

              <div className="bc-lead-customer">
                <b>{lead.name}</b>
                <small>{lead.email}</small>
              </div>

              <span>{lead.company}</span>
              <span>{lead.project}</span>
              <span>{lead.source}</span>

              <span
                className={`bc-lead-status ${lead.status.toLowerCase()}`}
              >
                {lead.status.replace("_", " ")}
              </span>

              <span>{formatDate(lead.createdAt)}</span>

              <div className="bc-lead-actions">
                <button
                  className="view"
                  title="View lead"
                  onClick={() => setSelectedLead(lead)}
                >
                  <Eye size={14} />
                </button>

                {lead.email !== "—" && (
                  <a
                    className="mail"
                    href={`mailto:${lead.email}`}
                    title="Email lead"
                  >
                    <Mail size={14} />
                  </a>
                )}

                {lead.phone !== "—" && (
                  <a
                    className="phone"
                    href={`tel:${lead.phone}`}
                    title="Call lead"
                  >
                    <Phone size={14} />
                  </a>
                )}
              </div>
            </div>
          ))}

          {!loading && filteredLeads.length === 0 && (
            <div className="bc-lead-empty">
              No leads match the selected criteria.
            </div>
          )}
        </section>
      </main>

      {selectedLead && (
        <div
          className="bc-lead-modal-backdrop"
          onClick={() => setSelectedLead(null)}
        >
          <div
            className="bc-lead-modal"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              className="bc-lead-modal-close"
              onClick={() => setSelectedLead(null)}
            >
              <X size={17} />
            </button>

            <span className="bc-lead-modal-eyebrow">
              {selectedLead.reference}
            </span>

            <h2>{selectedLead.name}</h2>

            <div className="bc-lead-detail-grid">
              <div>
                <span>Email</span>
                <strong>{selectedLead.email}</strong>
              </div>

              <div>
                <span>Phone</span>
                <strong>{selectedLead.phone}</strong>
              </div>

              <div>
                <span>Company</span>
                <strong>{selectedLead.company}</strong>
              </div>

              <div>
                <span>Project</span>
                <strong>{selectedLead.project}</strong>
              </div>

              <div>
                <span>Source</span>
                <strong>{selectedLead.source}</strong>
              </div>

              <div>
                <span>Status</span>
                <strong>{selectedLead.status}</strong>
              </div>
            </div>

            {(selectedLead.projectDetails ||
              selectedLead.project_details) && (
              <div className="bc-lead-notes">
                <span>Project Details</span>
                <p>
                  {selectedLead.projectDetails ||
                    selectedLead.project_details}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default LeadManagement;
